import React, { createContext, useContext, useRef, useState, useCallback } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

/**
 * Promise-based replacement for `window.confirm`.
 *
 * Usage (anywhere inside <ConfirmDialogProvider>):
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: "Delete?", confirmLabel: "Delete", danger: true }))) return;
 *
 * The provider mounts a single shadcn AlertDialog and reuses it across calls.
 */
const ConfirmContext = createContext(null);

export function ConfirmDialogProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({});
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    setOpts(options);
    setOpen(true);
    return new Promise((resolve) => { resolverRef.current = resolve; });
  }, []);

  const finish = (result) => {
    setOpen(false);
    const r = resolverRef.current;
    resolverRef.current = null;
    if (r) r(result);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={open}
        onOpenChange={(o) => { if (!o) finish(false); }}
      >
        <AlertDialogContent data-testid="confirm-dialog" className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">{opts.title || "Sei sicuro?"}</AlertDialogTitle>
            {opts.description && (
              <AlertDialogDescription>{opts.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => finish(false)} data-testid="confirm-cancel-btn">
              {opts.cancelLabel || "Annulla"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => finish(true)}
              data-testid="confirm-ok-btn"
              className={opts.danger ? "bg-fresh-red hover:bg-fresh-red/90 text-white" : "bg-zinc-950 hover:bg-zinc-800 text-white"}
            >
              {opts.confirmLabel || "Conferma"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmDialogProvider>");
  return ctx;
}
